import {AxiosResponse} from 'axios';
import {makeAutoObservable, onBecomeObserved, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';

import apiService from '../transport/apiService';
import {RewardsInformationResponse} from '../transport/types';
import {logger, serializeError} from '../utils/logger';

import type {RootStore} from './rootStore';

/**
 * Renders a mustache-style template string by replacing {{key}} with values from the provided object
 * @param template - Template string with {{variable}} placeholders
 * @param values - Object containing values to interpolate
 * @returns Rendered string with all placeholders replaced
 */
const renderTemplate = (template: string, values: Record<string, string>): string => {
  return template.replace(/\{\{(\s*[\w.]+\s*)\}\}/g, (_, key) => {
    const trimmedKey = key.trim();
    return values[trimmedKey] || '';
  });
};

const CONTENT_KEY_LOGGED_IN = 'JoinLoyaltyLoggedIn';
const CONTENT_KEY_LOGGED_OUT = 'JoinLoyalty';
const CONTENT_KEY_SHOP_REWARDS_NOT_AVAILABLE = 'JoinLoyaltyNotAvailable';
const CONTENT_KEY_JUST_FOR_YOU = 'RewardsPageJustForYou';
const CONTENT_KEY_PROMOTIONS = 'RewardsPagePromotions';

export {CONTENT_KEY_LOGGED_IN, CONTENT_KEY_LOGGED_OUT, CONTENT_KEY_SHOP_REWARDS_NOT_AVAILABLE};

type ContentKey =
  | typeof CONTENT_KEY_LOGGED_IN
  | typeof CONTENT_KEY_LOGGED_OUT
  | typeof CONTENT_KEY_JUST_FOR_YOU
  | typeof CONTENT_KEY_PROMOTIONS;

export class LoyaltyInformation {
  rootStore: RootStore;
  information: Record<ContentKey, LoyaltyInformationItem>;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    this.information = {
      [CONTENT_KEY_LOGGED_IN]: new LoyaltyInformationItem(rootStore, CONTENT_KEY_LOGGED_IN),
      [CONTENT_KEY_LOGGED_OUT]: new LoyaltyInformationItem(rootStore, CONTENT_KEY_LOGGED_OUT),
      [CONTENT_KEY_JUST_FOR_YOU]: new LoyaltyInformationItem(rootStore, CONTENT_KEY_JUST_FOR_YOU),
      [CONTENT_KEY_PROMOTIONS]: new LoyaltyInformationItem(rootStore, CONTENT_KEY_PROMOTIONS),
    };

    makeAutoObservable(this);
    makeLoggable(this);
  }

  reset = () => {
    (Object.keys(this.information) as ContentKey[]).map(key => {
      /**
       * onbecomeUnobserve not reliable so reset here
       */
      this.information[key] = new LoyaltyInformationItem(this.rootStore, key);
    });
  };
}

export class LoyaltyInformationItem {
  private requestPromise: Promise<AxiosResponse<RewardsInformationResponse>> | undefined;

  private rootStore: RootStore;
  private contentKey: string;

  isLoading: boolean = false;
  data: RewardsInformationResponse | null = null;

  constructor(rootStore: RootStore, contentKey: ContentKey) {
    this.rootStore = rootStore;
    this.contentKey = contentKey;
    makeAutoObservable(this);
    makeLoggable(this);

    /**
     * Fetch data lazily
     */
    onBecomeObserved(this, 'data', this.load);
  }

  load = async () => {
    if (!this.requestPromise) {
      this.requestPromise = apiService.getRewardsInformation(this.contentKey);
    }
    runInAction(() => {
      this.isLoading = true;
    });
    const {data} = await this.requestPromise;
    try {
      const {headline, headline2, headlineCopy} = data;
      const {userStore} = this.rootStore;

      /**
       * Templated with userStore data using mustache syntax {{variable}}
       */
      runInAction(() => {
        const values = {
          emailAddress: userStore.emailAddress || '',
          firstName: userStore.firstName || '',
          lastName: userStore.lastName || '',
        };
        this.data = {
          ...data,
          headline: renderTemplate(headline || '', values),
          headline2: renderTemplate(headline2 || '', values),
          headlineCopy: renderTemplate(headlineCopy || '', values),
        };
      });
    } catch (err) {
      logger.warn('LoyaltyInformationItem', 'Template rendering error, using fallback', {
        contentKey: this.contentKey,
        ...serializeError(err),
      });
      runInAction(() => {
        this.data = data;
        if (data.headlineCopy) {
          this.data.headlineCopy = data.headlineCopy.replace(
            '{{firstName}}',
            this.rootStore.userStore.firstName,
          );
        }
      });
    }
    runInAction(() => {
      this.isLoading = false;
    });
  };

  reset = () => {
    runInAction(() => {
      this.data = null;
      delete this.requestPromise;
    });
  };
}
